import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Box,
  Radio,
  RadioGroup,
  MenuItem,
} from "@mui/material";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import {
  Add,
  AddCircle,
  Delete,
  LocationOnOutlined,
  Remove,
} from "@mui/icons-material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
import {
  CenterEndPoints,
  createGeneralAPIEndpoint,
} from "../../../../services/General";

// ✅ Validation schema
const schema = yup.object().shape({
  title: yup.string().required("Title is required"),
  description: yup.string().required("Description is required"),
  centerId: yup.string().required("Center ID is required"),
  opensAtUtc: yup.date().required("Open date is required"),
  closesAtUtc: yup.date().required("Close date is required"),
  allowMultipleSubmissions: yup.boolean(),
  questions: yup
    .array()
    .of(
      yup.object().shape({
        questionText: yup.string().required("Question text is required"),
        options: yup
          .array()
          .of(yup.string().required("Option cannot be empty"))
          .min(2, "At least two options required"),
        points: yup
          .number()
          .typeError("Must be a number")
          .required("Points are required")
          .min(1, "Points must be between 1 and 10")
          .max(10, "Points must be between 1 and 10"),
      }),
    )
    .min(1, "At least one question is required"),
});
function AddHomeworkDialog({ open, onClose, onSubmit }) {
  const [centers, setCenters] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: "",
      description: "",
      type: 1,
      centerId: 0,
      opensAtUtc: "",
      closesAtUtc: "",
      allowMultipleSubmissions: true,
      questions: [
        {
          questionText: "",
          options: ["", "", "", ""],
          correctOptionsIndex: 0,
          points: 1,
        },
      ],
    },
  });
  const fetchCenters = () => {
    createGeneralAPIEndpoint(CenterEndPoints.all)
      .fetch()
      .then((res) => setCenters(res.data))
      .catch((err) => {
        console.error("Failed to fetch centers:", err);
        enqueueSnackbar("Failed to fetch centers.", { variant: "error" });
      });
  };

  useEffect(() => {
    fetchCenters();
  }, []);

  const {
    fields: questions,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "questions",
  });

  const handleFormSubmit = async (data) => {
    try {
      await onSubmit(data);

      reset();
    } catch (error) {
      console.error(error);
    }
  };
  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      className="add-assessment dialog"
    >
      <DialogTitle className="title">Create Homework</DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent dividers>
          <Box className="box-container">
            <Box className="box-items">
              <Controller
                name="title"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    className="textField"
                    {...field}
                    label="Title"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    margin="normal"
                  />
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    className="textField"
                    {...field}
                    label="Description"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    margin="normal"
                  />
                )}
              />
              <Controller
                name="centerId"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    className="textField select"
                    {...field}
                    select
                    label="Center"
                    margin="normal"
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  >
                    {centers.map((center) => (
                      <MenuItem
                        key={center.id}
                        value={center.id}
                        className="center-menu-item"
                      >
                        <div className="center-option">
                          <span className="center-title">
                            <span className="icon">
                              <LocationOnOutlined />
                            </span>{" "}
                            {center.name || "No center"}
                          </span>
                        </div>
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <Controller
                name="opensAtUtc"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    className="textField"
                    type="datetime-local"
                    label="Opens At"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    margin="normal"
                  />
                )}
              />

              <Controller
                name="closesAtUtc"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    className="textField"
                    type="datetime-local"
                    label="Closes At"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                  />
                )}
              />

              <Controller
                name="allowMultipleSubmissions"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label="Allow multiple submissions"
                  />
                )}
                margin="normal"
              />
            </Box>

            {/* Questions Section */}

            <Box>
              <span className="question-header">Questions</span>
              {questions.map((q, qIndex) => (
                <Box
                  key={q.id}
                  sx={{
                    border: "1px solid #ddd",
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                  }}
                >
                  <Box>
                    <Controller
                      name={`questions.${qIndex}.questionText`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={`Question ${qIndex + 1}`}
                          fullWidth
                          error={!!fieldState.error}
                          margin="normal"
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Box>
                  {/* Options */}

                  <Box>
                    <Controller
                      name={`questions.${qIndex}.correctOptionsIndex`}
                      control={control}
                      render={({ field: radioField, fieldState }) => (
                        <RadioGroup
                          value={radioField.value}
                          onChange={(e) =>
                            radioField.onChange(Number(e.target.value))
                          }
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        >
                          {q.options.map((_, optIndex) => (
                            <Box
                              key={optIndex}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mt: 1,
                              }}
                            >
                              <FormControlLabel
                                value={optIndex}
                                control={<Radio />}
                                label=""
                                sx={{ m: 0 }}
                              />

                              <Controller
                                name={`questions.${qIndex}.options.${optIndex}`}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <TextField
                                    {...field}
                                    fullWidth
                                    label={`Option ${optIndex + 1}`}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                  />
                                )}
                              />
                            </Box>
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </Box>

                  <Box className="form-grid-item">
                    <Controller
                      name={`questions.${qIndex}.points`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <Box display="flex" alignItems="center" gap={1}>
                          <IconButton
                            onClick={() =>
                              field.onChange(Math.max(1, field.value - 1))
                            }
                          >
                            <Remove />
                          </IconButton>
                          <TextField
                            {...field}
                            type="number"
                            label="Points"
                            fullWidth
                            margin="normal"
                            helperText={fieldState.error?.message}
                            error={!!fieldState.error}
                            inputProps={{
                              min: 1,
                              max: 10,
                              readOnly: true,
                            }}
                          />

                          <IconButton
                            onClick={() => field.onChange(field.value + 1)}
                          >
                            <Add />
                          </IconButton>
                        </Box>
                      )}
                    />
                  </Box>

                  <IconButton color="error" onClick={() => remove(qIndex)}>
                    <Delete />
                  </IconButton>
                </Box>
              ))}

              <Button
                variant="outlined"
                startIcon={<AddCircle />}
                onClick={() =>
                  append({
                    questionText: "",
                    options: ["", "", "", ""],
                    correctOptionsIndex: 0,
                    points: 1,
                  })
                }
              >
                Add Question
              </Button>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AddHomeworkDialog;
